import React, { useState, useEffect } from 'react';
import { cn } from 'lib/utils';
import {
  User,
  Trash2,
  AlertTriangle,
  Weight,
  Ruler,
  Droplet,
  Calendar,
  Heart,
  Crown,
  Settings,
  Sparkles,
  FlaskConical,
  RotateCcw,
  LogIn,
  LogOut,
  Cloud,
  Loader2,
  Download,
  Palette,
  FolderOpen,
  Activity,
  QrCode,
  CloudIcon,
  RefreshCw,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Switch } from 'components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { SubscriptionModal } from 'features/Subscription';
import { useSubscription } from '../../hooks/useSubscription';
import { apiService } from '../../services/api';
import { useOnboarding } from '../../context/OnboardingContext';
import { useAuth } from '../../context/AuthContext';
import { cloudSyncService } from '../../services/cloudSync';
import { downloadEirFile } from '../../utils';
import { AVAILABLE_THEMES, DEFAULT_THEME_ID, ThemeId, applyTheme, isValidThemeId, getStoredThemeMode } from '../../theme';
import { getLanguageFromBrowser, setLanguage, type Language } from '../../utils/language';
import { getErrorMessage } from '../../utils/errors';
import { formatDate } from '../../utils/date';
import { useGoogleDriveSync } from '../../hooks/useGoogleDriveSync';
import { SyncSettings } from '../ForYou/SyncSettings';
import { healthMemoryService } from '../../services/healthMemory';
import { persistence } from '../../services/persistence';
import type { HealthMemoryItem } from '../../types';

const PROFILE_STORAGE_KEY = 'eir-user-profile';

export interface UserProfile {
  name?: string;
  email?: string;
  dateOfBirth?: string;
  weight?: number;
  height?: number;
  bloodType?: string;
  allergies?: string;
  medications?: string;
  medicalConditions?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  notes?: string;
  preferredModel?: string;
  betaFeaturesEnabled?: boolean;
  cloudSyncEnabled?: boolean;
  agentModeEnabled?: boolean;
  memoryEnabled?: boolean;
  theme?: ThemeId;
}

const MODEL_OPTIONS = [
  { value: 'auto', label: 'Auto (Default - GPT-OSS 120B)' },
  { value: 'moonshotai/kimi-k2-instruct-0905', label: 'Kimi K2 Instruct 0905' },
  { value: 'openai/gpt-oss-120b', label: 'GPT-OSS 120B' },
  { value: 'berget:openai/gpt-oss-120b', label: 'GPT-OSS 120B (Berget AI)' }
];

const LANGUAGE_OPTIONS: { value: Language; label: string }[] = [
  { value: 'sv', label: 'Svenska' },
  { value: 'en', label: 'English (US)' },
];

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleteAllData: () => void | Promise<void>;
  onLoadFile?: () => void;
  onExport?: () => void;
  canExport?: boolean;
  onAnalyze?: () => void;
  canAnalyze?: boolean;
  analyzing?: boolean;
  onFetchRemoteEhr?: () => void;
  canFetchRemoteEhr?: boolean;
  fetchingRemoteEhr?: boolean;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({
  isOpen,
  onClose,
  onDeleteAllData,
  onLoadFile,
  onExport,
  canExport = false,
  onAnalyze,
  canAnalyze = false,
  analyzing = false,
  onFetchRemoteEhr,
  canFetchRemoteEhr = true,
  fetchingRemoteEhr = false
}) => {
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const { isPremium, subscription, refreshSubscription, loading: subscriptionLoading } = useSubscription();
  const { hasCompleted, startFeatureTour, startPersonalizedOnboarding } = useOnboarding();
  const {
    isConfigured: supabaseConfigured,
    user: authUser,
    session: authSession,
    loading: authLoading,
    signIn,
    signUp,
    signOut: signOutSupabase,
    error: authError,
  } = useAuth();

  const [profile, setProfile] = useState<UserProfile>(() => {
    const parsed = persistence.getJSON<UserProfile | null>(PROFILE_STORAGE_KEY, null);
    if (parsed && typeof parsed === 'object') {
      const themeId = isValidThemeId(parsed.theme) ? parsed.theme : DEFAULT_THEME_ID;
      return {
        ...parsed,
        theme: themeId,
        agentModeEnabled: parsed.agentModeEnabled !== false ? true : false,
        memoryEnabled: parsed.memoryEnabled === true
      };
    }
    return { theme: DEFAULT_THEME_ID, agentModeEnabled: true };
  });

  const [chatLanguage, setChatLanguage] = useState<Language>(() => getLanguageFromBrowser());
  const [authEmail, setAuthEmail] = useState(() => profile.email || '');
  const [authPassword, setAuthPassword] = useState('');
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [authState, setAuthState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [authMessage, setAuthMessage] = useState<string | null>(null);
  const [cloudSyncMessage, setCloudSyncMessage] = useState<string | null>(null);
  const [isDownloadingFromCloud, setIsDownloadingFromCloud] = useState(false);
  const selectedThemeId = isValidThemeId(profile.theme) ? profile.theme : DEFAULT_THEME_ID;

  const {
    isAuthenticated: isGoogleDriveAuthenticated,
    isSyncing: isGoogleDriveSyncing,
    lastSyncTime: googleDriveLastSyncTime,
    error: googleDriveSyncError,
    autoSyncEnabled: googleDriveAutoSyncEnabled,
    signIn: signInGoogleDrive,
    syncNow,
  } = useGoogleDriveSync();
  const [showGoogleDriveSyncSettings, setShowGoogleDriveSyncSettings] = useState(false);
  const [memoryItems, setMemoryItems] = useState<HealthMemoryItem[]>([]);

  useEffect(() => {
    if (!isGoogleDriveAuthenticated && showGoogleDriveSyncSettings) {
      setShowGoogleDriveSyncSettings(false);
    }
  }, [isGoogleDriveAuthenticated, showGoogleDriveSyncSettings]);

  useEffect(() => {
    applyTheme(selectedThemeId, getStoredThemeMode());
  }, [selectedThemeId]);

  useEffect(() => {
    if (isOpen) {
      setChatLanguage(getLanguageFromBrowser());
      void (async () => {
        setMemoryItems(await healthMemoryService.loadItems());
      })();
    }
  }, [isOpen]);

  useEffect(() => {
    const payload: UserProfile = {
      ...profile,
      theme: selectedThemeId
    };
    persistence.setJSON(PROFILE_STORAGE_KEY, payload);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('profileUpdated'));
    }
    if (profile.email) {
      persistence.removeItem('eir-subscription-status');
    }
  }, [profile, selectedThemeId]);

  useEffect(() => {
    if (authUser?.email) {
      setAuthEmail(authUser.email);
      setProfile((prev) => {
        if (prev.email) return prev;
        return { ...prev, email: authUser.email };
      });
    }
  }, [authUser?.email]);

  useEffect(() => {
    if (!authUser && profile.cloudSyncEnabled) {
      setProfile((prev) => ({ ...prev, cloudSyncEnabled: false }));
    }
  }, [authUser, profile.cloudSyncEnabled]);

  useEffect(() => {
    if (!profile.email && authEmail) {
      setProfile((prev) => ({ ...prev, email: prev.email ?? authEmail }));
    }
  }, [authEmail, profile.email]);

  const handleChange = (field: keyof UserProfile, value: string | number) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const formatLastSyncTime = (isoString: string): string => {
    const now = new Date();
    const syncTime = new Date(isoString);
    const diffMs = now.getTime() - syncTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatDate(syncTime);
  };

  const handleBooleanChange = (field: keyof UserProfile, value: boolean) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleAuthSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!authEmail.trim() || !authPassword.trim()) {
      setAuthState('error');
      setAuthMessage('Please enter both email and password.');
      return;
    }
    if (authPassword.length < 6) {
      setAuthState('error');
      setAuthMessage('Password must be at least 6 characters long.');
      return;
    }
    try {
      setAuthState('loading');
      setAuthMessage(null);
      if (authMode === 'signup') {
        await signUp(authEmail.trim(), authPassword);
        setAuthMessage('Account created successfully! You are now signed in.');
      } else {
        await signIn(authEmail.trim(), authPassword);
        setAuthMessage('Signed in successfully!');
      }
      setAuthState('success');
      setAuthPassword('');
      setTimeout(() => {
        setAuthMessage(null);
        setAuthState('idle');
      }, 3000);
    } catch (error) {
      console.error(`Failed to ${authMode}`, error);
      setAuthState('error');
      setAuthMessage(getErrorMessage(error, `Unable to ${authMode}. Please try again.`));
    }
  };

  const handleSignOut = async () => {
    await signOutSupabase();
    setCloudSyncMessage(null);
  };

  const handleCloudToggle = () => {
    if (!authUser) {
      setCloudSyncMessage('Sign in to Supabase to enable secure syncing.');
      return;
    }
    const nextValue = !profile.cloudSyncEnabled;
    handleBooleanChange('cloudSyncEnabled', nextValue);
    setCloudSyncMessage(nextValue
      ? 'Secure syncing enabled. Your latest data will back up automatically when the app is open.'
      : 'Secure syncing disabled. Data will remain stored locally only.');
    window.setTimeout(() => setCloudSyncMessage(null), 5000);
  };

  const handleThemeSelect = (themeId: ThemeId) => {
    setProfile(prev => ({ ...prev, theme: themeId }));
  };

  const handleDeleteAll = async () => {
    if (window.confirm('Are you sure you want to delete ALL data? This includes:\n\n- All EIR journal entries\n- All chat messages and summaries\n- All nutrition logs\n- All health assessments\n- All health actions\n- All profile information\n- All saved videos\n- All cloud backups (if logged in)\n\nThis action cannot be undone.')) {
      await onDeleteAllData();
      onClose();
    }
  };

  const handleStartFeatureTour = () => {
    startFeatureTour();
    onClose();
  };

  const handleStartPersonalizedOnboarding = () => {
    startPersonalizedOnboarding();
    onClose();
  };

  const tourBadgeText = hasCompleted ? 'Available anytime' : 'Perfect first step';

  const handleToggleBetaFeatures = () => {
    setProfile(prev => ({ ...prev, betaFeaturesEnabled: !prev.betaFeaturesEnabled }));
  };

  const handleToggleAgentMode = () => {
    setProfile(prev => ({ ...prev, agentModeEnabled: !prev.agentModeEnabled }));
  };

  const handleToggleMemory = () => {
    const nextValue = !profile.memoryEnabled;
    setProfile(prev => ({ ...prev, memoryEnabled: nextValue }));
    if (nextValue) {
      void (async () => {
        setMemoryItems(await healthMemoryService.loadItems());
      })();
    }
  };

  const handleConfirmMemory = (id: string) => {
    void (async () => {
      await healthMemoryService.updateStatus(id, 'user_confirmed');
      setMemoryItems(await healthMemoryService.loadItems());
    })();
  };

  const handleDismissMemory = (id: string) => {
    void (async () => {
      await healthMemoryService.updateStatus(id, 'dismissed');
      setMemoryItems(await healthMemoryService.loadItems());
    })();
  };

  const handleClearHealthMemory = () => {
    if (!window.confirm('Delete all saved health memory items? This cannot be undone.')) {
      return;
    }
    void (async () => {
      await healthMemoryService.clearAll();
      setMemoryItems([]);
    })();
  };

  const handleDownloadFromCloud = async () => {
    if (!authUser) {
      alert('Please sign in to download your cloud backup.');
      return;
    }
    setIsDownloadingFromCloud(true);
    try {
      const backupContent = await cloudSyncService.fetchEirBackup(authUser.id, authSession);
      if (!backupContent) {
        alert('No cloud backup found. Please ensure cloud sync is enabled and data has been synced.');
        return;
      }
      const { parseYAML } = await import('../../utils');
      const parsedData = parseYAML(backupContent);
      if (!parsedData || !parsedData.entries) {
        throw new Error('Invalid backup data format');
      }
      const notes = (parsedData.journal_entries || []).map((entry: any) => ({
        id: entry.id || `note-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        title: entry.title || '',
        content: entry.content || '',
        createdAt: entry.created_at || entry.createdAt || new Date().toISOString(),
        wellbeingScore: typeof entry.wellbeing_score === 'number' ? entry.wellbeing_score : undefined,
      }));
      await downloadEirFile(parsedData, notes);
      setCloudSyncMessage('Cloud backup downloaded successfully!');
      window.setTimeout(() => setCloudSyncMessage(null), 5000);
    } catch (error) {
      console.error('Failed to download from cloud:', error);
      alert(`Failed to download cloud backup: ${getErrorMessage(error, 'Unknown error')}`);
    } finally {
      setIsDownloadingFromCloud(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User size={24} />
              Profile & Settings
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-6">
            <div className="p-6 border rounded-lg grid gap-4 bg-muted">
              <h3 className="m-0 text-lg font-semibold text-foreground-strong flex items-center gap-2">
                <Settings size={18} />
                Data Management
              </h3>
              <div className="grid gap-3">
                {onLoadFile && (
                  <Button
                    type="button"
                    size="lg"
                    className="justify-start"
                    onClick={onLoadFile}
                  >
                    <FolderOpen size={22} className="mr-2" />
                    Load EIR File
                  </Button>
                )}
                {onExport && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start py-3.5 px-4 text-sm"
                    onClick={onExport}
                    disabled={!canExport}
                  >
                    <Download size={18} className="mr-2" />
                    Export
                  </Button>
                )}
                {onFetchRemoteEhr && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start py-3.5 px-4 text-sm"
                    onClick={onFetchRemoteEhr}
                    disabled={!canFetchRemoteEhr || fetchingRemoteEhr}
                  >
                    <QrCode size={18} className="mr-2" />
                    {fetchingRemoteEhr ? 'Opening BankID…' : 'Fetch from 1177'}
                  </Button>
                )}
                {onAnalyze && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start py-3.5 px-4 text-sm"
                    onClick={onAnalyze}
                    disabled={!canAnalyze || analyzing}
                  >
                    <Activity size={18} className="mr-2" />
                    {analyzing ? 'Analyzing...' : 'Health Analysis'}
                  </Button>
                )}
              </div>
            </div>

            <div className="p-6 border rounded-lg grid gap-4 bg-muted">
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 rounded-lg bg-primary text-primary-foreground inline-flex items-center justify-center">
                  <Sparkles size={22} />
                </span>
                <div className="flex flex-col gap-1">
                  <h3 className="m-0 text-lg font-semibold text-foreground-strong">Guided onboarding</h3>
                  <Badge variant="secondary">
                    {tourBadgeText}
                  </Badge>
                </div>
              </div>
              <p className="m-0 text-foreground-muted text-sm leading-relaxed">
                Get personalized recommendations based on your goals, or take a step-by-step walkthrough of all app features.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  type="button"
                  onClick={handleStartPersonalizedOnboarding}
                >
                  <Sparkles size={18} className="mr-2" />
                  Start Personalized Onboarding
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleStartFeatureTour}
                >
                  <Sparkles size={18} className="mr-2" />
                  Take Feature Tour
                </Button>
              </div>
            </div>

            <div className="p-5 border rounded-lg grid gap-4 bg-muted">
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-lg bg-secondary text-secondary-foreground inline-flex items-center justify-center">
                  <FlaskConical size={20} />
                </span>
                <div className="flex flex-col gap-1">
                  <h3 className="m-0 text-base font-semibold text-foreground-strong">Beta labs access</h3>
                  <Badge variant="muted">
                    {profile.betaFeaturesEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
              <p className="m-0 text-foreground-muted text-sm leading-relaxed">
                Unlock experimental experiences like Health Videos, Nutrition insights, Egenremiss tools, and Health Literacy guides.
                You can disable beta features anytime.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={handleToggleBetaFeatures}
              >
                <FlaskConical size={16} />
                {profile.betaFeaturesEnabled ? 'Disable beta features' : 'Enable beta features'}
              </Button>
            </div>

            <div className="p-5 border rounded-lg grid gap-4 bg-muted">
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-lg bg-secondary text-secondary-foreground inline-flex items-center justify-center">
                  <RotateCcw size={20} />
                </span>
                <div className="flex flex-col gap-1">
                  <h3 className="m-0 text-base font-semibold text-foreground-strong">Personalized setup</h3>
                </div>
              </div>
              <p className="m-0 text-foreground-muted text-sm leading-relaxed">
                Re-run the setup guide to update your health goals and get fresh personalized recommendations.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  persistence.removeItem('eir-personalized-onboarding-completed');
                  window.dispatchEvent(new CustomEvent('eir-trigger-personalized-onboarding'));
                  onClose();
                }}
              >
                <RotateCcw size={16} />
                Re-run setup guide
              </Button>
            </div>

            <div className="grid gap-4">
              <h3 className="m-0 text-lg font-semibold text-foreground-strong flex items-center gap-2">
                <User size={18} />
                Account & Sync
              </h3>
              <div className="border rounded-lg p-5 grid gap-4 bg-muted">
                {!supabaseConfigured ? (
                  <p className="m-0 text-sm text-foreground-strong leading-normal">
                    Supabase credentials are not configured. Add <code className="bg-muted px-1 rounded">REACT_APP_SUPABASE_URL</code> and <code className="bg-muted px-1 rounded">REACT_APP_SUPABASE_ANON_KEY</code> to enable secure sign-in and syncing.
                  </p>
                ) : authUser ? (
                  <>
                    <div className="flex items-center gap-2 font-semibold text-blue-900">
                      <LogIn size={18} />
                      Signed in with Supabase
                    </div>
                    <div className="grid gap-2 text-sm text-foreground-strong">
                      <span><strong>Email:</strong> {authUser.email ?? 'Connected via OAuth'}</span>
                      <span>Your workspace will stay in sync across devices while you are signed in.</span>
                      {cloudSyncMessage && <span className="text-sm">{cloudSyncMessage}</span>}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-fit"
                      onClick={handleSignOut}
                      disabled={authLoading}
                    >
                      <LogOut size={16} className="mr-2" />
                      Sign out
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 font-semibold text-blue-900">
                      <LogIn size={18} />
                      {authMode === 'signup' ? 'Create an account' : 'Sign in to protect your workspace'}
                    </div>
                    <p className="m-0 text-sm text-foreground-strong leading-normal">
                      {authMode === 'signup'
                        ? 'Create an account with email and password to sync feedback and keep your EIR data backed up safely.'
                        : 'Sign in with your email and password, or continue with Google or Facebook to sync feedback and keep your EIR data backed up safely.'}
                    </p>
                    <form className="flex flex-col gap-3" onSubmit={handleAuthSubmit}>
                      <div className="grid gap-2">
                        <Label htmlFor="auth-email">Email</Label>
                        <Input
                          id="auth-email"
                          type="email"
                          placeholder="you@example.com"
                          value={authEmail}
                          onChange={(e) => setAuthEmail(e.target.value)}
                          required
                          disabled={authState === 'loading'}
                          className="flex-1"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="auth-password">Password</Label>
                        <Input
                          id="auth-password"
                          type="password"
                          placeholder="Password"
                          value={authPassword}
                          onChange={(e) => setAuthPassword(e.target.value)}
                          required
                          disabled={authState === 'loading'}
                          minLength={6}
                          className="flex-1"
                        />
                      </div>
                      <Button type="submit" disabled={authState === 'loading'}>
                        {authState === 'loading' ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            {authMode === 'signup' ? 'Creating account…' : 'Signing in…'}
                          </>
                        ) : (
                          <>
                            <LogIn size={16} className="mr-2" />
                            {authMode === 'signup' ? 'Sign up' : 'Sign in'}
                          </>
                        )}
                      </Button>
                      <button
                        type="button"
                        className="text-primary text-sm font-medium underline cursor-pointer bg-transparent border-none mt-2"
                        onClick={() => {
                          setAuthMode(authMode === 'signin' ? 'signup' : 'signin');
                          setAuthMessage(null);
                          setAuthState('idle');
                        }}
                      >
                        {authMode === 'signin' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                      </button>
                      {(authMessage || authError) && (
                        <span className="text-sm text-red-600">{authMessage || authError}</span>
                      )}
                    </form>
                  </>
                )}
              </div>

              {supabaseConfigured && (
                <>
                  <div className="flex items-center justify-between gap-4 py-3 px-4 border rounded-lg bg-muted">
                    <div className="flex flex-col gap-1">
                      <strong className="flex items-center gap-1">
                        <Cloud size={16} />
                        Sync data across devices
                      </strong>
                      <span className="text-sm text-foreground">
                        Store your EIR data in the Cloud so your feedback and health data follow you.
                        <span className="block mt-2 text-red-700 font-bold">
                          IMPORTANT: When enabled, your health data IS stored in the cloud to enable sync across multiple devices. This includes all journal entries, notes, and health information.
                        </span>
                      </span>
                      {cloudSyncMessage && authUser && <span className="text-sm">{cloudSyncMessage}</span>}
                      {!authUser && <span className="text-sm text-foreground-muted">Sign in to enable secure cloud syncing.</span>}
                    </div>
                    <Switch
                      id="cloud-sync"
                      name="cloud-sync"
                      checked={Boolean(profile.cloudSyncEnabled && authUser)}
                      onCheckedChange={handleCloudToggle}
                      disabled={!authUser || authLoading}
                    />
                  </div>
                  {authUser && (
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleDownloadFromCloud}
                        disabled={isDownloadingFromCloud}
                        className="flex items-center gap-2"
                      >
                        {isDownloadingFromCloud ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download size={16} />
                            Download from Cloud
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="grid gap-4">
              <h3 className="m-0 text-lg font-semibold text-foreground-strong flex items-center gap-2">
                <CloudIcon size={18} />
                Google Drive Sync
              </h3>
              <div className="border rounded-lg p-6 bg-muted">
                {!isGoogleDriveAuthenticated ? (
                  <div className="rounded-lg p-6 bg-primary text-primary-foreground">
                    <div className="flex items-center gap-4 flex-wrap">
                      <CloudIcon size={24} />
                      <div className="flex-1">
                        <h4 className="m-0 mb-2 text-base font-semibold">Sync your data across devices</h4>
                        <p className="m-0 text-sm opacity-90 leading-normal">
                          Connect Google Drive to automatically backup and sync all your health data, journal entries, and progress.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={signInGoogleDrive}
                        disabled={isGoogleDriveSyncing}
                      >
                        <CloudIcon size={18} className="mr-2" />
                        Connect Google Drive
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center py-3 px-5 border rounded-lg mb-4 gap-4 flex-wrap bg-card">
                    <div className="flex items-center gap-3 text-foreground-strong">
                      <CloudIcon size={18} />
                      {isGoogleDriveSyncing ? (
                        <>
                          <RefreshCw size={16} className="animate-spin" />
                          <span className="text-sm text-foreground-muted">Syncing...</span>
                        </>
                      ) : googleDriveSyncError ? (
                        <>
                          <AlertCircle size={16} className="text-red-500" />
                          <span className="text-sm text-red-500">{googleDriveSyncError}</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle size={16} className="text-emerald-500" />
                          <span className="text-sm text-foreground-muted">
                            Synced with Google Drive
                            {googleDriveLastSyncTime && ` (${formatLastSyncTime(googleDriveLastSyncTime)})`}
                          </span>
                        </>
                      )}
                      {googleDriveAutoSyncEnabled && !isGoogleDriveSyncing && (
                        <span className="py-1 px-2.5 bg-muted rounded-md text-xs font-medium text-foreground-muted">Auto-sync enabled</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => syncNow()}
                        disabled={isGoogleDriveSyncing}
                        title="Sync now"
                      >
                        <RefreshCw size={18} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowGoogleDriveSyncSettings(true)}
                        title="Sync settings"
                      >
                        <Settings size={18} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {showGoogleDriveSyncSettings && (
                <SyncSettings
                  onClose={() => setShowGoogleDriveSyncSettings(false)}
                  key={isGoogleDriveAuthenticated ? 'authenticated' : 'not-authenticated'}
                />
              )}
            </div>

            <div className="grid gap-4">
              <h3 className="m-0 text-lg font-semibold text-foreground-strong flex items-center gap-2">
                <Palette size={18} />
                Color theme
              </h3>
              <div className="grid gap-4">
                {AVAILABLE_THEMES.map((theme) => {
                  const isSelected = selectedThemeId === theme.id;
                  return (
                    <button
                      key={theme.id}
                      type="button"
                      className={cn(
                        'flex flex-col gap-2.5 items-start py-4 px-4 rounded-lg border text-left transition-all',
                        isSelected ? 'border-primary bg-muted' : 'border hover:border-border-strong'
                      )}
                      onClick={() => handleThemeSelect(theme.id)}
                      aria-pressed={isSelected}
                      aria-label={`Activate ${theme.name} theme`}
                    >
                      <div className="flex w-full items-center justify-between gap-2">
                        <span className="text-base font-semibold text-foreground-strong">{theme.name}</span>
                        {isSelected && (
                          <Badge variant="default">Active</Badge>
                        )}
                      </div>
                      <p className="m-0 text-sm leading-normal text-foreground-muted">{theme.description}</p>
                      <div className="flex gap-1.5 items-center">
                        {theme.preview.map((color) => (
                          <span
                            key={color}
                            className="w-7 h-7 rounded-lg"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-4">
              <h3 className="m-0 text-lg font-semibold text-foreground-strong flex items-center gap-2">
                <Settings size={18} />
                AI Preferences
              </h3>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <Settings size={16} />
                    Preferred AI Model
                  </Label>
                  <select
                    className="p-3 border rounded-lg text-sm bg-card text-foreground cursor-pointer"
                    value={profile.preferredModel || 'auto'}
                    onChange={(e) => handleChange('preferredModel', e.target.value)}
                  >
                    {MODEL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="m-0 mt-2 text-xs text-foreground-muted">
                    Auto uses the default GPT-OSS 120B model. Choose another option to override it for chats and summaries.
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    Chat response language
                  </Label>
                  <select
                    className="p-3 border rounded-lg text-sm bg-card text-foreground cursor-pointer"
                    value={chatLanguage}
                    onChange={(e) => {
                      const lang = e.target.value as Language;
                      if (lang === 'en' || lang === 'sv') {
                        setLanguage(lang);
                        setChatLanguage(lang);
                        window.dispatchEvent(new CustomEvent('languageChanged'));
                      }
                    }}
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="m-0 mt-2 text-xs text-foreground-muted">
                    Choose the language for AI chat responses. Swedish or US English.
                  </p>
                </div>
                <div className="flex items-center justify-between gap-4 py-3 px-4 border rounded-lg bg-muted">
                  <div className="flex flex-col gap-1">
                    <strong className="flex items-center gap-1">
                      <Sparkles size={16} />
                      Agent Mode
                    </strong>
                    <span className="text-sm text-foreground-muted">
                      Enable agent mode for enhanced AI capabilities. Agent mode allows the AI to perform actions like adding calendar events and journal entries automatically.
                    </span>
                  </div>
                  <Switch
                    id="agent-mode"
                    name="agent-mode"
                    checked={Boolean(profile.agentModeEnabled)}
                    onCheckedChange={handleToggleAgentMode}
                  />
                </div>
                <div className="flex items-center justify-between gap-4 py-3 px-4 border rounded-lg bg-muted">
                  <div className="flex flex-col gap-1">
                    <strong className="flex items-center gap-1">
                      <Activity size={16} />
                      Health Memory
                    </strong>
                    <span className="text-sm text-foreground-muted">
                      Save user-owned memory from conversations to improve personalization over time. Stored locally by default.
                    </span>
                  </div>
                  <Switch
                    id="health-memory"
                    name="health-memory"
                    checked={Boolean(profile.memoryEnabled)}
                    onCheckedChange={handleToggleMemory}
                  />
                </div>
                {profile.memoryEnabled && (
                  <div className="border rounded-lg p-4 grid gap-3">
                    <div className="flex items-center justify-between gap-3">
                      <strong>Saved Health Memory ({memoryItems.length})</strong>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleClearHealthMemory}
                        disabled={memoryItems.length === 0}
                      >
                        Clear all
                      </Button>
                    </div>
                    {memoryItems.length === 0 ? (
                      <p className="m-0 text-sm text-foreground-muted">
                        No health memory captured yet. Continue chatting and confirm relevant findings.
                      </p>
                    ) : (
                      <div className="grid gap-2 max-h-48 overflow-y-auto pr-1">
                        {memoryItems.slice(0, 30).map((item) => (
                          <div key={item.id} className="border rounded-md p-3 grid gap-2">
                            <div className="flex items-center justify-between gap-2">
                              <Badge variant="outline">{item.category}</Badge>
                              <span className="text-xs text-foreground-muted">
                                {item.status} · {Math.round(item.confidence * 100)}%
                              </span>
                            </div>
                            <div className="text-sm">
                              <strong>{item.label}</strong>
                              {item.detail ? <p className="m-0 mt-1 text-foreground-muted">{item.detail}</p> : null}
                            </div>
                            <div className="flex gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleConfirmMemory(item.id)}>
                                Confirm
                              </Button>
                              <Button type="button" size="sm" variant="ghost" onClick={() => handleDismissMemory(item.id)}>
                                Incorrect
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid gap-4">
              <h3 className="m-0 text-lg font-semibold text-foreground-strong flex items-center gap-2">Personal Information</h3>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <User size={16} />
                    Name
                  </Label>
                  <Input
                    type="text"
                    value={profile.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Your name"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <User size={16} />
                    Email <span className="text-primary font-semibold">*</span>
                  </Label>
                  <Input
                    type="email"
                    value={profile.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="your@email.com"
                    required
                  />
                  <p className="m-0 mt-2 text-xs text-foreground-muted">
                    Required for subscription management and billing
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <Calendar size={16} />
                    Date of Birth
                  </Label>
                  <Input
                    type="date"
                    value={profile.dateOfBirth || ''}
                    onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <h3 className="m-0 text-lg font-semibold text-foreground-strong flex items-center gap-2">Health Metrics</h3>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <Weight size={16} />
                    Weight (kg)
                  </Label>
                  <Input
                    type="number"
                    value={profile.weight || ''}
                    onChange={(e) => handleChange('weight', parseFloat(e.target.value) || 0)}
                    placeholder="e.g., 70"
                    min={0}
                    step={0.1}
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <Ruler size={16} />
                    Height (cm)
                  </Label>
                  <Input
                    type="number"
                    value={profile.height || ''}
                    onChange={(e) => handleChange('height', parseFloat(e.target.value) || 0)}
                    placeholder="e.g., 175"
                    min={0}
                    step={0.1}
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <Droplet size={16} />
                    Blood Type
                  </Label>
                  <select
                    className="p-3 border rounded-lg text-sm bg-card text-foreground cursor-pointer w-full"
                    value={profile.bloodType || ''}
                    onChange={(e) => handleChange('bloodType', e.target.value)}
                  >
                    <option value="">Select blood type</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <h3 className="m-0 text-lg font-semibold text-foreground-strong flex items-center gap-2">Medical Information</h3>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Allergies
                  </Label>
                  <Textarea
                    value={profile.allergies || ''}
                    onChange={(e) => handleChange('allergies', e.target.value)}
                    placeholder="List any allergies (e.g., Penicillin, Nuts, Latex)"
                    className="min-h-[100px]"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <Heart size={16} />
                    Current Medications
                  </Label>
                  <Textarea
                    value={profile.medications || ''}
                    onChange={(e) => handleChange('medications', e.target.value)}
                    placeholder="List current medications and dosages"
                    className="min-h-[100px]"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Medical Conditions
                  </Label>
                  <Textarea
                    value={profile.medicalConditions || ''}
                    onChange={(e) => handleChange('medicalConditions', e.target.value)}
                    placeholder="List any chronic conditions or medical history"
                    className="min-h-[100px]"
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <h3 className="m-0 text-lg font-semibold text-foreground-strong flex items-center gap-2">Emergency Contact</h3>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label>Contact Name</Label>
                  <Input
                    type="text"
                    value={profile.emergencyContact || ''}
                    onChange={(e) => handleChange('emergencyContact', e.target.value)}
                    placeholder="Emergency contact name"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    value={profile.emergencyPhone || ''}
                    onChange={(e) => handleChange('emergencyPhone', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <h3 className="m-0 text-lg font-semibold text-foreground-strong flex items-center gap-2">Additional Notes</h3>
              <div className="grid gap-2">
                <Textarea
                  value={profile.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Any additional health information or notes"
                  className="min-h-[100px]"
                />
              </div>
            </div>

            {!isPremium && (
              <div className="p-6 border border-amber-400 rounded-xl grid gap-4 bg-amber-50">
                <div className="flex items-center gap-2 text-amber-800 font-semibold text-lg">
                  <Crown size={20} />
                  Upgrade to Premium
                </div>
                <p className="m-0 text-amber-800 text-sm leading-relaxed">
                  Unlock unlimited AI health analysis, assessments, nutrition tracking, and advanced insights.
                </p>
                <Button
                  type="button"
                  className="bg-primary text-white font-semibold w-full justify-center"
                  onClick={() => setShowSubscriptionModal(true)}
                >
                  <Crown size={18} className="mr-2" />
                  Upgrade to Premium
                </Button>
              </div>
            )}

            {isPremium && subscription && (
              <div className="p-6 border border-emerald-500 rounded-xl grid gap-4 bg-emerald-50">
                <div className="flex items-center gap-2 text-emerald-800 font-semibold text-lg">
                  <Crown size={20} />
                  Premium Active
                </div>
                <div className="inline-flex items-center gap-2 py-2 px-4 bg-emerald-600 text-white rounded-lg font-semibold text-sm">
                  <Crown size={16} />
                  {subscription.plan === 'monthly' ? 'Monthly Plan' : 'Annual Plan'}
                </div>
                <p className="m-0 text-emerald-800 text-sm mb-3">
                  {subscription.expiresAt && (
                    <>Renews on {formatDate(subscription.expiresAt)}</>
                  )}
                </p>
                <Button
                  variant="secondary"
                  className="w-full justify-center bg-emerald-600 text-white border-none hover:bg-emerald-700"
                  onClick={async () => {
                    if (!profile.email) {
                      alert('Please add your email address to manage your subscription.');
                      return;
                    }
                    try {
                      const portalResponse = await apiService.createPortalSession(profile.email);
                      if (portalResponse.url) {
                        window.location.href = portalResponse.url;
                      }
                    } catch (error: unknown) {
                      console.error('Error opening customer portal:', error);
                      let errorMessage = 'Failed to open subscription management. Please try again or contact support.';
                      try {
                        const errorData = typeof error === 'object' && error !== null && 'message' in error ? error : null;
                        const msg = errorData && typeof (errorData as { message?: string }).message === 'string' ? (errorData as { message: string }).message : '';
                        if (msg.includes('404') || msg.includes('No subscription found')) {
                          errorMessage = 'Subscription not found. Please contact support or try refreshing your subscription status.';
                        } else if (msg.includes('not configured') || msg.includes('configuration')) {
                          const setupUrl = (errorData as { setupUrl?: string })?.setupUrl || 'https://dashboard.stripe.com/test/settings/billing/portal';
                          errorMessage = `Customer Portal needs to be configured. Please set it up in Stripe Dashboard:\n\n${setupUrl}\n\nClick "Activate test link" for test mode.`;
                          alert(errorMessage);
                          window.open(setupUrl, '_blank');
                          return;
                        }
                      } catch {
                        // Fall through
                      }
                      alert(errorMessage);
                    }
                  }}
                >
                  <Settings size={16} className="mr-2" />
                  Manage Subscription
                </Button>
              </div>
            )}

            {!isPremium && profile.email && (
              <div className="p-4 bg-blue-50 rounded-lg border-blue-200">
                <p className="m-0 mb-3 text-blue-900 font-medium text-sm">
                  Subscription Status
                </p>
                <p className="m-0 mb-3 text-xs text-foreground-muted">
                  Email: <strong>{profile.email}</strong>
                </p>
                <Button
                  variant="secondary"
                  onClick={() => refreshSubscription()}
                  disabled={subscriptionLoading}
                  className="w-full justify-center"
                >
                  {subscriptionLoading ? 'Checking...' : 'Refresh Subscription Status'}
                </Button>
              </div>
            )}

            <div className="p-6 bg-red-50 border-red-200 rounded-xl grid gap-4">
              <div className="flex items-center gap-2 text-red-600 font-semibold">
                <AlertTriangle size={20} />
                Danger Zone
              </div>
              <p className="m-0 text-red-800 text-[0.875rem]">
                Permanently delete all data stored in this application. This action cannot be undone.
              </p>
              <Button
                type="button"
                variant="destructive"
                className="bg-red-100 border-red-200 text-red-600 hover:bg-red-200 w-fit"
                onClick={handleDeleteAll}
              >
                <Trash2 size={16} className="mr-2" />
                Delete All Data
              </Button>
            </div>
          </div>

          <div className="flex justify-end items-center p-6 border-t gap-4">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />
    </>
  );
};
